import { ReactNode } from "react"
import { Alert, Box, Button, Icons, Stack, Text } from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"

/**
 * Tells the user what needs fixing after a hotel configuration (room types,
 * meal plans, child age bands) is changed.
 */
export function HotelConfigActionRequiredAlert({
	afterAction,
	rateSheetStep,
	hasChannelManager,
}: {
	afterAction: string
	rateSheetStep: string
	hasChannelManager: boolean
}) {
	return (
		<Alert status="warning" title="ACTION REQUIRED">
			<Stack gap="1">
				<Text>After {afterAction}, you&apos;ll need to:</Text>
				<Box as="ul" listStyleType="disc" marginLeft="4">
					{hasChannelManager ? (
						<li>
							Fix the Hotel mapping in your channel manager and re-sync ARI
							details
						</li>
					) : null}
					<li>Fix all the B2B Rate Sheets to {rateSheetStep}</li>
				</Box>
			</Stack>
		</Alert>
	)
}

export type TUnlockHotelConfigCopy = {
	title: string
	action: string
	afterAction: string
	rateSheetStep: string
	acceptLabel: string
	hasChannelManager: boolean
}

/**
 * Warns that changing a hotel configuration needs follow-up work, and calls
 * `onSuccess` once the user confirms. The caller renders the trigger through
 * `children`.
 */
export function UnlockHotelConfigInDialog({
	title,
	action,
	afterAction,
	rateSheetStep,
	acceptLabel,
	onSuccess,
	children,
	hasChannelManager,
}: TUnlockHotelConfigCopy & {
	onSuccess: () => void
	children: (props: { open: () => void }) => ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} title={title} sm>
				<Dialog.Body>
					<Stack gap="4">
						<Text>
							Are you sure you want to{" "}
							<Text as="span" fontWeight="semibold">
								{action}
							</Text>
							?
						</Text>
						<HotelConfigActionRequiredAlert
							afterAction={afterAction}
							rateSheetStep={rateSheetStep}
							hasChannelManager={hasChannelManager}
						/>
					</Stack>
				</Dialog.Body>
				<Dialog.Footer>
					<Button
						level="primary"
						status="danger"
						onClick={() => {
							close()
							onSuccess()
						}}
					>
						{acceptLabel}
					</Button>
				</Dialog.Footer>
			</Dialog>
		</>
	)
}

/**
 * Toggle for a locked hotel configuration. Unlocking asks for confirmation
 * first, locking again does not. `label` names what is locked, e.g. "room".
 */
export function HotelConfigLockButton({
	label,
	isLocked,
	onUnlock,
	onLock,
	...unlockCopy
}: TUnlockHotelConfigCopy & {
	label?: string
	isLocked: boolean
	onUnlock: () => void
	onLock: () => void
}) {
	const configuration = label ? `${label} configuration` : "configuration"
	return isLocked ? (
		<UnlockHotelConfigInDialog {...unlockCopy} onSuccess={onUnlock}>
			{({ open }) => (
				<Button type="button" level="tertiary" onClick={open}>
					<Icons.Lock />
					Unlock {configuration}
				</Button>
			)}
		</UnlockHotelConfigInDialog>
	) : (
		<Button type="button" level="tertiary" status="success" onClick={onLock}>
			<Icons.Lock />
			Lock {configuration}
		</Button>
	)
}
